import { useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Search, User } from 'lucide-react';
import type { TreeData } from '@/api/tree.api';
import { suchePerson } from './aufdecken';

// ─── Vorfahren aufdecken ─────────────────────────────────────────────────────
//
// Suche nach einer Person im geladenen Baum; die Wahl deckt ihre Ahnenreihe auf
// (Eltern, deren Eltern, …) und nichts daneben. Gesucht wird lokal: die Daten
// sind für die Zeichnung ohnehin im Browser.

interface VorfahrenDialogProps {
  daten: TreeData;
  onClose: () => void;
  onWaehle: (personId: string) => void;
}

export function VorfahrenDialog({ daten, onClose, onWaehle }: VorfahrenDialogProps) {
  const [eingabe, setEingabe] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const treffer = useMemo(() => suchePerson(daten, eingabe), [daten, eingabe]);

  useEffect(() => {
    const vorher = document.activeElement as HTMLElement | null;
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => vorher?.focus?.();
  }, []);

  // Escape schließt nur diesen Dialog (capture + stopPropagation wie bei der
  // Schnellsuche), damit Listener dahinter nicht mitfeuern.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const lebensdaten = (n: TreeData['nodes'][number]) =>
    [n.birthDateText && `* ${n.birthDateText}`, n.deathDateText && `† ${n.deathDateText}`]
      .filter(Boolean)
      .join('  ');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => (treffer.length ? (i + 1) % treffer.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (treffer.length ? (i - 1 + treffer.length) % treffer.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const n = treffer[index];
      if (n) onWaehle(n.id);
    }
  };

  const keineTreffer = eingabe.trim().length > 0 && treffer.length === 0;

  return (
    <div
      className="fixed inset-0 z-[1450] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Vorfahren aufdecken"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-4 pb-1 pt-3">
          <p className="text-sm font-semibold text-slate-800">Vorfahren aufdecken</p>
          <p className="text-xs text-slate-500">
            Person wählen – ihre Eltern, deren Eltern und so weiter werden aufgedeckt.
          </p>
          <div className="flex items-center gap-2.5 py-2.5">
            <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={eingabe}
              onChange={(e) => {
                setEingabe(e.target.value);
                setIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Person suchen…"
              aria-label="Person suchen"
              role="combobox"
              aria-expanded="true"
              aria-controls="vorfahren-liste"
              aria-autocomplete="list"
              aria-activedescendant={treffer.length ? `vorfahren-option-${index}` : undefined}
              className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
            <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:inline-block">
              esc
            </kbd>
          </div>
        </div>

        <div id="vorfahren-liste" role="listbox" aria-label="Treffer" className="max-h-80 overflow-y-auto py-1.5">
          {treffer.map((n, i) => {
            const aktiv = i === index;
            const name = `${n.firstName} ${n.lastName ?? ''}`.trim();
            const daten_ = lebensdaten(n) || n.birthPlace;
            return (
              <button
                key={n.id}
                id={`vorfahren-option-${i}`}
                role="option"
                aria-selected={aktiv}
                onMouseEnter={() => setIndex(i)}
                onClick={() => onWaehle(n.id)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${
                  aktiv ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                }`}
              >
                <User
                  className={`h-4 w-4 shrink-0 ${aktiv ? 'text-blue-500' : 'text-slate-400'}`}
                  aria-hidden="true"
                />
                <span className="truncate font-medium">{name}</span>
                {daten_ && <span className="ml-auto shrink-0 text-xs text-slate-400">{daten_}</span>}
              </button>
            );
          })}
          {keineTreffer && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Keine Person gefunden.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
          <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
          Aufdecken
        </div>
      </div>
    </div>
  );
}
